import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import './styles/App.css';

const socket = io(
  process.env.REACT_APP_BACKEND_URL ||
  (window.location.hostname === 'localhost' ? 'http://localhost:5000' : window.location.origin)
);

const ADVISOR_COLORS = [
  '#10a37f', '#d97706', '#4285f4', '#1e293b',
  '#7c3aed', '#0ea5e9', '#dc2626', '#059669'
];

function colorFor(advisorId, panel) {
  const idx = panel.findIndex(a => a.id === advisorId);
  return ADVISOR_COLORS[(idx >= 0 ? idx : 0) % ADVISOR_COLORS.length];
}

function initialOf(name = '') {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

// Strip the [CONSENSUS: YES|NO] marker from streaming text so the user never sees it.
const MARKER_RE = /\[\s*CONSENSUS\s*:\s*(YES|NO)\s*\]/i;
function stripMarker(text) {
  return text.replace(MARKER_RE, '').replace(/\s+$/, '');
}

function App() {
  const [question, setQuestion] = useState('');
  const [discussion, setDiscussion] = useState(null);
  const [messages, setMessages] = useState([]); // flat chronological list
  const [voteTally, setVoteTally] = useState(null); // {yesCount, total, turn}
  const [history, setHistory] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [finalAnswer, setFinalAnswer] = useState({ content: '', status: 'idle' }); // 'idle' | 'streaming' | 'done' | 'error'
  const streamRef = useRef(null);

  // Auto-scroll to bottom on new content
  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    const onCreated = (data) => {
      setDiscussion({
        id: data.discussionId,
        question: data.question,
        panel: data.panel || [],
        status: 'active',
        finalResult: null
      });
      setMessages([]);
      setVoteTally(null);
      setFinalAnswer({ content: '', status: 'idle' });
    };

    const onAdvisorStart = (data) => {
      setMessages(prev => {
        if (prev.some(m => m.responseId === data.responseId)) return prev;
        return [...prev, {
          responseId: data.responseId,
          advisorId: data.advisorId,
          advisorName: data.advisorName,
          model: data.model,
          turnNumber: data.turnNumber,
          content: '',
          status: 'streaming',
          vote: null,
          startedAt: data.timestamp
        }];
      });
    };

    const onAdvisorChunk = (data) => {
      setMessages(prev => prev.map(m =>
        m.responseId === data.responseId
          ? { ...m, content: stripMarker(m.content + (data.delta || '')) }
          : m
      ));
    };

    const onAdvisorComplete = (data) => {
      setMessages(prev => prev.map(m =>
        m.responseId === data.responseId
          ? {
              ...m,
              content: stripMarker(data.content || m.content),
              vote: data.consensusVote || null,
              status: data.success ? 'done' : 'error'
            }
          : m
      ));
    };

    const onConsensusVote = (data) => {
      setVoteTally({ yesCount: data.yesCount, total: data.total, turn: data.turn });
    };

    const onFinalAnswerStart = () => {
      setFinalAnswer({ content: '', status: 'streaming' });
    };

    const onFinalAnswerChunk = (data) => {
      setFinalAnswer(prev => ({
        content: prev.content + (data.delta || ''),
        status: 'streaming'
      }));
    };

    const onFinalAnswerComplete = (data) => {
      setFinalAnswer({
        content: data.content || '',
        status: data.success ? 'done' : 'error'
      });
    };

    const onFinalized = (data) => {
      setDiscussion(prev => prev && prev.id === data.discussionId
        ? { ...prev, finalResult: data.finalResult, status: 'completed' }
        : prev
      );
      setHistory(prev => [{
        id: data.discussionId,
        question: discussion?.question || '(unknown)',
        timestamp: new Date().toISOString(),
        finalResult: data.finalResult
      }, ...prev]);
    };

    socket.on('discussionCreated', onCreated);
    socket.on('advisorStart', onAdvisorStart);
    socket.on('advisorChunk', onAdvisorChunk);
    socket.on('advisorComplete', onAdvisorComplete);
    socket.on('consensusVote', onConsensusVote);
    socket.on('finalAnswerStart', onFinalAnswerStart);
    socket.on('finalAnswerChunk', onFinalAnswerChunk);
    socket.on('finalAnswerComplete', onFinalAnswerComplete);
    socket.on('discussionFinalized', onFinalized);

    return () => {
      socket.off('discussionCreated', onCreated);
      socket.off('advisorStart', onAdvisorStart);
      socket.off('advisorChunk', onAdvisorChunk);
      socket.off('advisorComplete', onAdvisorComplete);
      socket.off('consensusVote', onConsensusVote);
      socket.off('finalAnswerStart', onFinalAnswerStart);
      socket.off('finalAnswerChunk', onFinalAnswerChunk);
      socket.off('finalAnswerComplete', onFinalAnswerComplete);
      socket.off('discussionFinalized', onFinalized);
    };
  }, [discussion?.question]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await fetch('/api/advisory/discuss', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, userId: 'user' })
      });
      if (!response.ok) throw new Error('Failed to start discussion');
      await response.json();
      setQuestion('');
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const panel = discussion?.panel || [];
  const currentSpeaker = messages.find(m => m.status === 'streaming');
  const isComplete = discussion?.status === 'completed';

  return (
    <div className="App">
      <header className="app-header">
        <h1>LLM Group Chat</h1>
        <p>Eight AI advisors discuss until they converge on a shared answer</p>
      </header>

      <main className="main-content">
        <section className="input-section">
          <form onSubmit={handleSubmit} className="question-form">
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ask a question for the group to discuss..."
              rows="3"
              disabled={isSubmitting}
            />
            <button type="submit" disabled={isSubmitting || !question.trim()}>
              {isSubmitting ? 'Starting…' : 'Submit Question'}
            </button>
          </form>
        </section>

        {discussion && (
          <section className="results-section">
            <div className="discussion-header">
              <h2>"{discussion.question}"</h2>
              <div className="status-row">
                {isComplete ? (
                  <span className={`status-pill ${discussion.finalResult?.isConsensusReached ? 'done' : 'idle'}`}>
                    {discussion.finalResult?.isConsensusReached
                      ? `✓ Consensus reached after ${messages.length} turns`
                      : `Discussion ended after ${messages.length} turns`}
                  </span>
                ) : currentSpeaker ? (
                  <span className="status-pill live">
                    <span className="pulse-dot" /> Turn {messages.length} — {currentSpeaker.advisorName} is speaking
                  </span>
                ) : (
                  <span className="status-pill">Preparing…</span>
                )}
                {voteTally && !isComplete && (
                  <span className="status-pill vote">
                    Last rotation: {voteTally.yesCount}/{voteTally.total} agreed
                  </span>
                )}
              </div>
            </div>

            {panel.length > 0 && (
              <div className="panel-strip">
                {panel.map(a => {
                  const last = [...messages].reverse().find(m => m.advisorId === a.id);
                  let cls = 'idle';
                  if (last?.status === 'streaming') cls = 'streaming';
                  else if (last?.vote === 'YES') cls = 'voted-yes';
                  else if (last?.vote === 'NO') cls = 'voted-no';
                  else if (last?.status === 'done') cls = 'done';
                  return (
                    <div key={a.id} className={`panel-chip status-${cls}`}>
                      <span className="avatar" style={{ background: colorFor(a.id, panel) }}>
                        {initialOf(a.name)}
                      </span>
                      <span className="panel-name">{a.name}</span>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="stream" ref={streamRef}>
              {messages.length === 0 && (
                <div className="empty-state">Waiting for the first advisor to speak…</div>
              )}
              {messages.map(m => (
                <div key={m.responseId} className={`bubble status-${m.status}`}>
                  <div
                    className="avatar lg"
                    style={{ background: colorFor(m.advisorId, panel) }}
                  >{initialOf(m.advisorName)}</div>
                  <div className="bubble-body">
                    <div className="bubble-header">
                      <span className="bubble-name">{m.advisorName}</span>
                      <span className="bubble-model">{m.model}</span>
                      <span className="bubble-turn">#{m.turnNumber}</span>
                      {m.status === 'streaming' && <span className="typing-tag">typing…</span>}
                      {m.status === 'error' && <span className="error-tag">error</span>}
                      {m.status === 'done' && m.vote === 'YES' && (
                        <span className="vote-tag yes">agrees</span>
                      )}
                      {m.status === 'done' && m.vote === 'NO' && (
                        <span className="vote-tag no">wants more discussion</span>
                      )}
                    </div>
                    <div className="bubble-content">
                      {m.content}
                      {m.status === 'streaming' && <span className="caret" />}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {(finalAnswer.status !== 'idle' || (isComplete && discussion.finalResult)) && (
              <div className={`final-result ${discussion?.finalResult?.isConsensusReached === false ? 'no-consensus' : ''}`}>
                <h3>
                  {discussion?.finalResult?.isConsensusReached === false
                    ? 'Final Answer (no strong consensus)'
                    : 'Final Answer'}
                  {finalAnswer.status === 'streaming' && <span className="typing-tag"> writing…</span>}
                </h3>
                <p className="final-answer">
                  {finalAnswer.content || discussion?.finalResult?.finalAnswer || ''}
                  {finalAnswer.status === 'streaming' && <span className="caret" />}
                </p>
                {isComplete && discussion.finalResult && (
                  <>
                    <p><strong>Agreement level:</strong> {Math.round((discussion.finalResult.consensusLevel || 0) * 100)}%</p>
                    <p className="final-reasoning">{discussion.finalResult.reasoning}</p>
                  </>
                )}
              </div>
            )}
          </section>
        )}

        {history.length > 0 && (
          <section className="history-section">
            <h3>Previous Discussions</h3>
            <ul>
              {history.map((item, idx) => (
                <li key={idx} className="history-item">
                  <div className="history-question">"{item.question}"</div>
                  <div className="history-summary">
                    {item.finalResult?.finalAnswer?.substring(0, 140) + '…'}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}

export default App;
